import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Stack,
  Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Modal from '../../../components/common/Modal';
import { CalculateUserProfilePercentage } from '../../../helper/commonHelper';
import {
  getStudentOfferCategory,
  getStudentOfferFromCMS
} from '../../../service/student.offers.service';
import Page from '../../../components/Page';
import { SET_OFFERS, SET_CATEGORY } from '../../../redux/offer.reducer';
import SimpleOfferCard from '../../../components/SimpleOfferCard';
import palette from '../../../theme/palette';
import useResponsive from '../../../hooks/useResponsive';
import Iconify from '../../../fl-components/Iconify';

export default function StudentOffers() {
  // Offer reducer
  const offerData = useSelector((state) => state.offers.data);
  // Offer category
  const offerCategories = useSelector((state) => state.offers.categories);
  // Flag for display loading screen
  const [isLoading, setIsLoading] = useState(offerData === null);
  // Current logged in user
  const currentUser = useSelector((state) => state.auth.currentUser);
  // Selected category
  const [selectedCategory, setSelectedCategory] = useState(null);
  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const dispatch = useDispatch();
  const routeState = useLocation();
  const isDesktop = useResponsive('up', 'md');

  useEffect(() => {
    if (routeState && routeState?.state?.category) {
      setIsLoading(true);
      setSelectedCategory(() => {
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return routeState?.state?.category;
      });
    }
  }, [routeState]);

  // Get profile completion count from 1 to 100.
  const userCompletedProfile = useMemo(
    () => CalculateUserProfilePercentage(currentUser),
    [currentUser]
  );

  useEffect(() => {
    // Fetch offers for student.
    async function fetchOffers() {
      const offerResponse = await getStudentOfferFromCMS();
      if (offerResponse) {
        dispatch({ type: SET_OFFERS, payload: offerResponse });
        setIsLoading(false);
      } else {
        dispatch({ type: SET_OFFERS, payload: [] });
        setIsLoading(false);
      }
    }

    async function fetchCategories() {
      const offerCategory = await getStudentOfferCategory();
      if (offerCategory) {
        dispatch({ type: SET_CATEGORY, payload: offerCategory });
        setSelectedCategory(offerCategory[0]);
      }
    }

    if (offerData === null) fetchOffers();
    if (offerCategories === null) fetchCategories();
    else if (routeState?.state?.category) {
      setSelectedCategory(routeState?.state?.category);
    } else setSelectedCategory(offerCategories[0]);
  }, []);

  return (
    <Page
      title="Offers"
      style={{
        display: `${isLoading ? 'flex' : ''}`,
        width: `${isLoading ? '100%' : ''}`,
        height: `${isLoading ? '100%' : ''}`,
        justifyContent: `${isLoading ? 'center' : ''}`,
        alignItems: `${isLoading ? 'center' : ''}`
      }}
    >
      <Container sx={{ height: '500px' }}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <Box mb={2} pb={2}>
            {isDesktop ? (
              <Stack
                direction="row"
                textAlign="center"
                justifyContent="center"
                spacing={2}
                mb={2}
                sx={{
                  position: 'sticky',
                  zIndex: 10,
                  top: { md: -10, lg: 0 },
                  background: 'rgba(255, 255, 255, 0.72)',
                  backdropFilter: 'blur(6px)'
                }}
              >
                {offerCategories?.map((category) => (
                  <Box
                    key={category._id}
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      ...(selectedCategory?.category_name.toLowerCase() ===
                        category.category_name.toLowerCase() && {
                        '&:after': {
                          content: '""',
                          borderBottom: '4px solid',
                          borderColor: palette.primary.main,
                          width: `calc(100% - ${category.category_name.length}px)`,
                          display: 'block',
                          margin: '0 auto',
                          borderTopRightRadius: 20,
                          borderTopLeftRadius: 20
                        }
                      })
                    }}
                  >
                    {/* <img
                    src={`https://cms.fainlearn.com${category.icon[0].url}`}
                    alt={category.category_name}
                    height={25}
                    style={{
                      color: palette.primary.main
                    }}
                  /> */}
                    <Typography
                      mt={0.8}
                      pb={1}
                      color={
                        selectedCategory?.category_name.toLowerCase() ===
                        category.category_name.toLowerCase()
                          ? palette.primary.main
                          : ''
                      }
                      fontWeight="600"
                      sx={{
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category.category_name}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Box textAlign="end" mb={2}>
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="bx:filter" />}
                  onClick={() => setModalOpen(true)}
                >
                  Filter
                </Button>
              </Box>
            )}
            <Grid container spacing={2}>
              {offerData
                .filter((data) => {
                  if (selectedCategory?.category_name?.toLowerCase() === 'all') return data;
                  if (data?.Coupon_Category)
                    return data?.Coupon_Category.toLowerCase().includes(
                      selectedCategory?.category_name?.toLowerCase()
                    );
                  return null;
                })
                .map((od, index) => (
                  <Grid key={od.id} item xs={12} sm={6} md={4}>
                    <SimpleOfferCard
                      offer={od}
                      lockOverlay={index > 2 && userCompletedProfile !== 100}
                    />
                  </Grid>
                ))}
            </Grid>
          </Box>
        )}
      </Container>

      {/* Category mobile modal */}
      {modalOpen && (
        <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Filter Offer">
          {offerCategories.map((category) => (
            <Chip
              label={category.category_name}
              key={category._id}
              variant="outlined"
              color={
                selectedCategory?.category_name.toLowerCase() ===
                category.category_name.toLowerCase()
                  ? 'primary'
                  : 'default'
              }
              sx={{ mr: 1, mb: 1 }}
              onClick={() => setSelectedCategory(category)}
            />
          ))}
        </Modal>
      )}
    </Page>
  );
}
